/**
 * Content List
 *
 * Search filtering and A-Z alphabet navigation for the Content List block.
 * Accessible: counter uses aria-live, inactive letters are non-focusable,
 * and focus management follows WCAG patterns.
 */

const ACTIVE_CLASS = "is-active";
const HIDDEN_CLASS = "is-hidden";
const INACTIVE_CLASS = "is-inactive";
const TABLET_BP = "(max-width: 850px)";
const SCROLL_OFFSET = 140; // matches CSS sticky top / scroll-margin-top

/**
 * Initialize all content list blocks on the page.
 */
function initContentLists() {
  const sections = document.querySelectorAll(".content-list");

  sections.forEach((section) => {
    const items = Array.from(section.querySelectorAll(".list-item"));
    if (!items.length) return;

    initSearch(section, items);

    if (section.classList.contains("has-alpha-nav")) {
      initAlphaNav(section, items);
    }
  });
}

// ---------------------------------------------------------------------------
// Search
// ---------------------------------------------------------------------------

function initSearch(section, items) {
  const input = section.querySelector(".content-list-search-input");
  if (!input) return;

  const counterEl = section.querySelector(".content-list-counter-count");
  const listEl = section.querySelector(".list-items");

  // Prefer a server-rendered no-results block so the empty state can carry
  // its illustration/heading; fall back to a plain paragraph otherwise.
  let noResults = section.querySelector(".content-list-no-results");
  if (!noResults) {
    noResults = document.createElement("p");
    noResults.className = "content-list-no-results";
    noResults.textContent = "No results found.";
    noResults.setAttribute("role", "status");
    if (listEl) listEl.after(noResults);
  }
  noResults.hidden = true;

  // Prevent form submission (search is live, no server round-trip).
  const form = input.closest("form");
  if (form) {
    form.addEventListener("submit", (e) => e.preventDefault());
  }

  input.addEventListener("input", () => {
    const query = input.value.trim().toLowerCase();

    let visibleCount = 0;

    items.forEach((item) => {
      const text = getSearchableText(item);
      const matches = !query || text.includes(query);

      item.classList.toggle(HIDDEN_CLASS, !matches);

      if (matches) visibleCount++;
    });

    // Update counter.
    if (counterEl) {
      counterEl.textContent = String(visibleCount);
    }

    // Toggle no-results message.
    noResults.hidden = visibleCount > 0;

    // If alpha nav is present, refresh which letters are active.
    const alphaNav = document.querySelector(
      ".has-alpha-nav-layout > .alpha-nav",
    );
    const mobileNav = section.querySelector(".alpha-nav-mobile");
    if (alphaNav || mobileNav) {
      const activeLetters = getActiveLetters(items);
      if (alphaNav) updateAlphaNavState(alphaNav, activeLetters);
      if (mobileNav) updateMobileNavState(mobileNav, activeLetters);
    }
  });
}

/**
 * Gather searchable text from a list item (headline, subhead, body).
 */
function getSearchableText(item) {
  const parts = [];
  const headline = item.querySelector(".list-item-headline");
  const subhead = item.querySelector(".list-item-subhead");
  const body = item.querySelector(".list-item-body");

  if (headline) parts.push(headline.textContent);
  if (subhead) parts.push(subhead.textContent);
  if (body) parts.push(body.textContent);

  return parts.join(" ").toLowerCase();
}

// ---------------------------------------------------------------------------
// A-Z Alphabet Navigation
// ---------------------------------------------------------------------------

function initAlphaNav(section, items) {
  const layout = document.querySelector(".has-alpha-nav-layout");
  if (!layout) return;

  const activeLetters = getActiveLetters(items);

  injectLetterAnchors(items);

  const nav = buildAlphaNav(activeLetters);
  const mobileNav = buildMobileAlphaNav(activeLetters);

  // Desktop nav: prepend into the shared layout wrapper (sibling to .side-nav-content).
  layout.prepend(nav);

  // Mobile nav: inside the block, before toolbar or list.
  const toolbar = section.querySelector(".content-list-toolbar");
  const listEl = section.querySelector(".list-items");
  const insertBefore = toolbar || listEl;
  if (insertBefore) {
    insertBefore.before(mobileNav);
  }

  // Set initial visibility based on breakpoint.
  const mql = window.matchMedia(TABLET_BP);

  function handleBreakpoint(e) {
    mobileNav.hidden = !e.matches;
    // Desktop nav visibility handled by CSS display:none
  }

  mql.addEventListener("change", handleBreakpoint);
  handleBreakpoint(mql);

  // Scroll tracking for active letter (desktop).
  let ticking = false;

  function onScroll() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => {
      updateActiveLetterOnScroll(section, nav);
      ticking = false;
    });
  }

  window.addEventListener("scroll", onScroll, { passive: true });

  // Recalculate blob position on resize.
  window.addEventListener("resize", () => {
    const active = nav.querySelector(
      "." + ACTIVE_CLASS + ":not(." + INACTIVE_CLASS + ")",
    );
    if (active && nav._blob && nav._itemsContainer) {
      snapBlob(nav._blob, nav._itemsContainer, active);
    }
  });

  // Set initial active state.
  updateActiveLetterOnScroll(section, nav);

  // External code (e.g. the client-mode filter-bar) can dispatch
  // 'content-list:items-changed' on the section after toggling .is-hidden
  // on items, and the alpha-nav will refresh which letters are reachable.
  section.addEventListener("content-list:items-changed", () => {
    const activeLetters = getActiveLetters(items);
    repositionLetterAnchors(items);
    updateAlphaNavState(nav, activeLetters);
    if (mobileNav) updateMobileNavState(mobileNav, activeLetters);
  });
}

/**
 * Get a Set of letters that have visible items.
 */
function getActiveLetters(items) {
  const letters = new Set();

  items.forEach((item) => {
    if (item.classList.contains(HIDDEN_CLASS)) return;
    const letter = item.dataset.letter;
    if (letter) letters.add(letter);
  });

  return letters;
}

/**
 * Inject invisible anchor targets before the first item of each letter group.
 * Runs once on init; `repositionLetterAnchors` keeps them in the right <li>
 * as filtering hides/shows items.
 */
function injectLetterAnchors(items) {
  const seen = new Set();

  items.forEach((item) => {
    const letter = item.dataset.letter;
    if (!letter || seen.has(letter)) return;
    seen.add(letter);

    const anchor = document.createElement("span");
    anchor.id = `letter-${letter}`;
    anchor.className = "alpha-anchor";
    anchor.setAttribute("aria-hidden", "true");

    item.style.position = "relative";
    item.prepend(anchor);
  });
}

/**
 * Move each letter's anchor span into the first non-hidden <li> with that
 * letter. Without this, an anchor sitting in a now-hidden first item leaves
 * the alpha-nav clicks landing nowhere reachable.
 */
function repositionLetterAnchors(items) {
  const placed = new Set();

  items.forEach((item) => {
    if (item.classList.contains(HIDDEN_CLASS)) return;
    const letter = item.dataset.letter;
    if (!letter || placed.has(letter)) return;
    placed.add(letter);

    const anchor = document.getElementById(`letter-${letter}`);
    if (!anchor) return;
    if (anchor.parentNode === item) return;

    item.style.position = "relative";
    item.prepend(anchor);
  });
}

// ---------------------------------------------------------------------------
// Alpha-nav blob animation (mirrors sidenav.js liquid blob)
// ---------------------------------------------------------------------------

const BLOB_SIZE = 8;
const BLOB_ANIM_DURATION = 150;

function easeInCubic(t) {
  return t * t * t;
}

function easeOutCubic(t) {
  return 1 - Math.pow(1 - t, 3);
}

let blobAnimId = null;
let currentBlobY = null;
const prefersReducedMotion = window.matchMedia(
  "(prefers-reduced-motion: reduce)",
);

function getBlobPosition(itemsContainer, link) {
  const indicator = link.querySelector(".alpha-nav-indicator");
  if (!indicator) return null;
  const containerRect = itemsContainer.getBoundingClientRect();
  const indicatorRect = indicator.getBoundingClientRect();
  return {
    x: indicatorRect.left - containerRect.left,
    y: indicatorRect.top - containerRect.top,
  };
}

function snapBlob(blob, itemsContainer, link) {
  if (blobAnimId) {
    cancelAnimationFrame(blobAnimId);
    blobAnimId = null;
  }
  const pos = getBlobPosition(itemsContainer, link);
  if (!pos) return;
  blob.style.left = pos.x + "px";
  blob.style.top = pos.y + "px";
  blob.style.height = BLOB_SIZE + "px";
  blob.style.width = BLOB_SIZE + "px";
  blob.style.opacity = 1;
  currentBlobY = pos.y;
}

function animateBlob(blob, itemsContainer, link) {
  const pos = getBlobPosition(itemsContainer, link);
  if (!pos) return;

  blob.style.left = pos.x + "px";

  if (currentBlobY === null || prefersReducedMotion.matches) {
    snapBlob(blob, itemsContainer, link);
    return;
  }

  const fromY = currentBlobY;
  const toY = pos.y;
  if (fromY === toY) return;

  if (blobAnimId) {
    cancelAnimationFrame(blobAnimId);
    blobAnimId = null;
  }

  currentBlobY = toY;
  const movingDown = toY > fromY;
  const startTime = performance.now();

  function tick(now) {
    const elapsed = now - startTime;
    const t = Math.min(elapsed / BLOB_ANIM_DURATION, 1);

    const leading = easeOutCubic(t);
    const trailing = easeInCubic(t);

    let top, bottom;
    if (movingDown) {
      top = fromY + (toY - fromY) * trailing;
      bottom = fromY + BLOB_SIZE + (toY - fromY) * leading;
    } else {
      top = fromY + (toY - fromY) * leading;
      bottom = fromY + BLOB_SIZE + (toY - fromY) * trailing;
    }

    const height = Math.max(BLOB_SIZE, bottom - top);
    const stretch = (height - BLOB_SIZE) / Math.abs(toY - fromY);

    blob.style.top = top + "px";
    blob.style.height = height + "px";
    blob.style.width = BLOB_SIZE * (1 - stretch * 0.35) + "px";
    blob.style.opacity = 1 - stretch * 0.4;

    if (t < 1) {
      blobAnimId = requestAnimationFrame(tick);
    } else {
      blobAnimId = null;
    }
  }

  blobAnimId = requestAnimationFrame(tick);
}

/**
 * Build the desktop A-Z nav element.
 */
function buildAlphaNav(activeLetters) {
  const nav = document.createElement("nav");
  nav.className = "alpha-nav";
  nav.setAttribute("aria-label", "Alphabetical navigation");

  const itemsContainer = document.createElement("div");
  itemsContainer.className = "alpha-nav-items";

  const blob = document.createElement("span");
  blob.className = "alpha-nav-blob";
  blob.setAttribute("aria-hidden", "true");
  itemsContainer.appendChild(blob);

  // Store references on the nav element for use in scroll tracking.
  nav._blob = blob;
  nav._itemsContainer = itemsContainer;

  const alphabet = "abcdefghijklmnopqrstuvwxyz";

  alphabet.split("").forEach((letter) => {
    const isActive = activeLetters.has(letter);

    if (isActive) {
      const link = document.createElement("a");
      link.href = `#letter-${letter}`;
      link.className = "alpha-nav-link";

      const indicator = document.createElement("span");
      indicator.className = "alpha-nav-indicator";
      indicator.setAttribute("aria-hidden", "true");
      link.appendChild(indicator);

      const label = document.createElement("span");
      label.textContent = letter.toUpperCase();
      link.appendChild(label);

      link.addEventListener("click", (e) => {
        e.preventDefault();
        const target = document.getElementById(`letter-${letter}`);
        if (!target) return;

        target.scrollIntoView({ behavior: "smooth", block: "start" });
        history.replaceState(null, "", `#letter-${letter}`);

        // Move focus to the first visible item in this letter group.
        const parentItem = target.closest(".list-item");
        if (parentItem) {
          const headline = parentItem.querySelector(".list-item-headline");
          const focusTarget = headline || parentItem;
          if (!focusTarget.hasAttribute("tabindex")) {
            focusTarget.setAttribute("tabindex", "-1");
          }
          setTimeout(() => focusTarget.focus({ preventScroll: true }), 400);
        }
      });

      itemsContainer.appendChild(link);
    } else {
      const span = document.createElement("span");
      span.className = `alpha-nav-link ${INACTIVE_CLASS}`;

      const indicator = document.createElement("span");
      indicator.className = "alpha-nav-indicator";
      indicator.setAttribute("aria-hidden", "true");
      span.appendChild(indicator);

      const label = document.createElement("span");
      label.textContent = letter.toUpperCase();
      span.appendChild(label);

      span.setAttribute("aria-disabled", "true");
      itemsContainer.appendChild(span);
    }
  });

  nav.appendChild(itemsContainer);
  return nav;
}

/**
 * Build the mobile select dropdown.
 */
function buildMobileAlphaNav(activeLetters) {
  const wrapper = document.createElement("div");
  wrapper.className = "alpha-nav-mobile";
  wrapper.hidden = true; // JS will show/hide based on breakpoint

  const label = document.createElement("label");
  label.className = "screen-reader-text";
  label.textContent = "Jump to letter";

  const select = document.createElement("select");
  select.setAttribute("aria-label", "Jump to letter");

  const alphabet = "abcdefghijklmnopqrstuvwxyz";

  // Default option
  const defaultOpt = document.createElement("option");
  defaultOpt.value = "";
  defaultOpt.textContent = "Jump to\u2026";
  defaultOpt.disabled = true;
  defaultOpt.selected = true;
  select.appendChild(defaultOpt);

  alphabet.split("").forEach((letter) => {
    if (!activeLetters.has(letter)) return;

    const option = document.createElement("option");
    option.value = letter;
    option.textContent = letter.toUpperCase();
    select.appendChild(option);
  });

  select.addEventListener("change", () => {
    const letter = select.value;
    if (!letter) return;

    const target = document.getElementById(`letter-${letter}`);
    if (target) {
      target.scrollIntoView({ behavior: "smooth", block: "start" });

      const parentItem = target.closest(".list-item");
      if (parentItem) {
        const headline = parentItem.querySelector(".list-item-headline");
        const focusTarget = headline || parentItem;
        if (!focusTarget.hasAttribute("tabindex")) {
          focusTarget.setAttribute("tabindex", "-1");
        }
        setTimeout(() => focusTarget.focus({ preventScroll: true }), 400);
      }
    }
  });

  wrapper.appendChild(label);
  wrapper.appendChild(select);

  return wrapper;
}

/**
 * Update active letter in the desktop nav based on scroll position.
 */
function updateActiveLetterOnScroll(section, nav) {
  const items = section.querySelectorAll(
    ".list-item[data-letter]:not(.is-hidden)",
  );
  if (!items.length) return;

  let activeLetter = null;

  for (const item of items) {
    if (item.getBoundingClientRect().top <= SCROLL_OFFSET) {
      activeLetter = item.dataset.letter;
    }
  }

  // If nothing has scrolled past the offset, use the first visible letter.
  if (!activeLetter) {
    activeLetter = items[0].dataset.letter;
  }

  if (activeLetter) {
    setActiveNavLetter(nav, activeLetter);
  }
}

/**
 * Set the active letter in the desktop alpha nav.
 */
function setActiveNavLetter(nav, letter) {
  const links = nav.querySelectorAll(".alpha-nav-link");
  let activeLink = null;

  links.forEach((link) => {
    const linkLetter = link.textContent.trim().toLowerCase();
    const isMatch = linkLetter === letter;
    link.classList.toggle(ACTIVE_CLASS, isMatch);
    if (isMatch && !link.classList.contains(INACTIVE_CLASS)) {
      activeLink = link;
    }
  });

  if (activeLink && nav._blob && nav._itemsContainer) {
    animateBlob(nav._blob, nav._itemsContainer, activeLink);
  }
}

/**
 * Update the desktop alpha nav: enable/disable letters based on visible items.
 */
function updateAlphaNavState(nav, activeLetters) {
  const links = nav.querySelectorAll(".alpha-nav-link");

  links.forEach((el) => {
    const letter = el.textContent.trim().toLowerCase();
    const shouldBeActive = activeLetters.has(letter);

    if (shouldBeActive && el.tagName === "SPAN") {
      // Swap span → link.
      const link = document.createElement("a");
      link.href = `#letter-${letter}`;
      link.className = "alpha-nav-link";

      const indicator = document.createElement("span");
      indicator.className = "alpha-nav-indicator";
      indicator.setAttribute("aria-hidden", "true");
      link.appendChild(indicator);

      const label = document.createElement("span");
      label.textContent = letter.toUpperCase();
      link.appendChild(label);

      link.addEventListener("click", (e) => {
        e.preventDefault();
        const target = document.getElementById(`letter-${letter}`);
        if (target) {
          target.scrollIntoView({ behavior: "smooth", block: "start" });
          setActiveNavLetter(nav, letter);
        }
      });

      el.replaceWith(link);
    } else if (!shouldBeActive && el.tagName === "A") {
      // Swap link → span.
      const span = document.createElement("span");
      span.className = `alpha-nav-link ${INACTIVE_CLASS}`;

      const indicator = document.createElement("span");
      indicator.className = "alpha-nav-indicator";
      indicator.setAttribute("aria-hidden", "true");
      span.appendChild(indicator);

      const label = document.createElement("span");
      label.textContent = letter.toUpperCase();
      span.appendChild(label);

      span.setAttribute("aria-disabled", "true");
      el.replaceWith(span);
    } else if (!shouldBeActive) {
      el.classList.add(INACTIVE_CLASS);
      el.setAttribute("aria-disabled", "true");
    } else {
      el.classList.remove(INACTIVE_CLASS);
      el.removeAttribute("aria-disabled");
    }
  });
}

/**
 * Update the mobile select dropdown: rebuild options based on visible items.
 */
function updateMobileNavState(mobileNav, activeLetters) {
  const select = mobileNav.querySelector("select");
  if (!select) return;

  const currentValue = select.value;

  // Remove all non-default options.
  while (select.options.length > 1) {
    select.remove(1);
  }

  const alphabet = "abcdefghijklmnopqrstuvwxyz";

  alphabet.split("").forEach((letter) => {
    if (!activeLetters.has(letter)) return;

    const option = document.createElement("option");
    option.value = letter;
    option.textContent = letter.toUpperCase();
    select.appendChild(option);
  });

  // Restore selection if letter still exists.
  if (activeLetters.has(currentValue)) {
    select.value = currentValue;
  }
}

document.addEventListener("DOMContentLoaded", initContentLists);
